import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { COLORS } from './Mobile';
import Textarea from 'components/bootstrap/forms/Textarea';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Button from 'components/bootstrap/Button';
import showNotification from 'components/extras/showNotification';
import { usePlanningData } from './hooks/usePlanningData';
import SvgGallery from 'assets/customizeIcons/svg-icons/GalleryIcon';
import { deleteAPI, postAPI } from 'apis/backend/apiNode';
import AuthContext from 'contexts/authContext';
import RemoveIcon from 'assets/customizeIcons/remove.svg';
import AddIcon from 'assets/customizeIcons/AddIcon.svg';
import TrashIcon from 'assets/customizeIcons/svg-icons/TrashIcon';
import { formatCurrency } from 'pages/comptabilite/utils/functions';
import moment from 'moment';
import AddPaiementLineModal from './AddPaiementLineModal';
import { deletePythonAPI, getAllAttchmentsAPI, getAPIById, getSingleAttachementAPI } from 'apis/backend/apiPython';
import ImageVisualisation from 'components/ImageVisualisation';

const PaiementTabMobile = ({ setInterventionData, interventionData }) => {
	const [paiementPython, setPaiementPython] = useState([]);
	const [attachmentsMap, setAttachmentsMap] = useState({});
	const [loading, setLoading] = useState(false);
	const [selectedImage, setSelectedImage] = useState()

	const { updateIntervention } = usePlanningData();
	const { userData } = useContext(AuthContext);
	const [createModel, setCreateModel] = useState(false);
	const fetchAttachmentsForPayment = useCallback(async (paymentId) => {
		if (!paymentId) return [];
		const moduleName = "paiements";
		try {
			const result = await getAllAttchmentsAPI(moduleName, paymentId);
			if (result && Array.isArray(result?.files)) {
				return result.files.map((fileName) => ({
					id: fileName,
					url: getSingleAttachementAPI(moduleName, paymentId, fileName),
					isNew: false,
				}));
			}
		} catch (err) {
			console.error(`Erreur fichiers pour ${paymentId}:`, err);
		}
		return [];
	}, []);
	// 1. LOAD DATA FROM BOTH SOURCES
	useEffect(() => {
		const loadPaiements = async () => {
			const legacyPaiements = interventionData?.rapport_intervention?.paiements || [];
			const newIds = interventionData?.rapport_intervention?.paiement_ids || [];

			// Combine unique IDs
			const allUniqueIds = [...new Set([
				...legacyPaiements.map(p => p.payment_id).filter(id => id),
				...newIds
			])];

			if (allUniqueIds.length === 0) return;
			setLoading(true);

			try {
				const promises = allUniqueIds.map(async (id) => {
					// Fetch payment details
					const res = await getAPIById("paiements", id);
					// Fetch attachments for the image
					const files = await fetchAttachmentsForPayment(id);
					return { data: res, files: files, id };
				});

				const results = await Promise.all(promises);

				const newPythonList = results.map(r => r.data).filter(Boolean);
				const newFilesMap = {};
				results.forEach(r => { if (r.files) newFilesMap[r.id] = r.files });

				setPaiementPython(newPythonList);
				setAttachmentsMap(newFilesMap);
			} catch (err) {
				console.error("Error loading paiements:", err);
			} finally {
				setLoading(false);
			}
		};

		loadPaiements();
	}, [interventionData?.rapport_intervention?.paiement_ids]);

	const handleDeletePaiement = async (pythonPaymentId) => {
		if (!window.confirm('Confirmer la suppression définitive ?')) return;

		try {
			// Delete from Python
			await deletePythonAPI('paiements', `/${pythonPaymentId}`);

			// Update MongoDB (Remove from paiement_ids)
			const updatedIds = (interventionData?.rapport_intervention?.paiement_ids || [])
				.filter(id => id !== pythonPaymentId);

			const updatePayload = {
				rapport_intervention: {
					...(interventionData?.rapport_intervention || {}),
					paiement_ids: updatedIds,
				},
				updatedBy: userData?.id,
			};

			await updateIntervention(interventionData?._id, updatePayload);

			// Update Local State
			setInterventionData(prev => ({
				...prev,
				rapport_intervention: { ...prev.rapport_intervention, paiement_ids: updatedIds }
			}));
			setPaiementPython(prev => prev.filter(p => p.id !== pythonPaymentId));

			showNotification('Succès', 'Paiement supprimé', 'info');
		} catch (error) {
			console.error("Delete error:", error);
		}
	};

	return (
		<div className='devis-container'>
			<div className='devis-header'>
				<h3>Paiements</h3>
				<button className='btn-create' onClick={() => setCreateModel(true)}>
					<img src={AddIcon} width={25} height={25} />
					Ajouter
				</button>
			</div>

			{loading ? (
				<div className='text-center p-5'><Spinner animation="border" /></div>
			) : !!paiementPython.length ? (
				<div className='paiement-cards-grid' style={{ padding: '10px' }}>
					{paiementPython.map((item) => {
						const files = attachmentsMap[item.id] || [];
						const coverImage = files.length > 0 ? files[0].url : null;
						const isDraft = item.state === 'draft';

						return (
							<div key={item.id} className="paiement-card" style={styles.card}>
								{/* PHOTO SECTION */}
								<div style={styles.imageContainer}>
									{coverImage ? (
										<img src={coverImage} alt="paiement" style={styles.image} onClick={() => setSelectedImage(files[0])} />
									) : (
										<div className='text-muted small'>Pas d'image</div>
									)}
								</div>

								{/* CONTENT SECTION */}
								<div style={styles.content}>
									<div style={{ flex: 1 }}>
										<h4 style={styles.amount}>{formatCurrency(item.amount)} </h4>
										<p style={styles.subText}>{item?.sorte} - {item.payment_method_id?.name || item.payment_method} </p>
										<small style={styles.date}>
											{moment(item.date_payment || item.date_paiement).format('DD/MM/YYYY')}
										</small>
									</div>

									{/* DELETE ONLY IF DRAFT */}
									{isDraft && (
										<Button
											variant='link'
											onClick={() => handleDeletePaiement(item.id)}
											style={{ padding: 5 }}
										>
											<TrashIcon size={22} color={COLORS.ERROR} />
										</Button>
									)}
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className='empty-text'>Aucun paiement enregistré</div>
			)}

			{createModel && (
				<AddPaiementLineModal
					setInterventionData={setInterventionData}
					interventionData={interventionData}
					setVisible={setCreateModel}
				/>
			)}

			{!!selectedImage && (
				<ImageVisualisation selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
			)}
		</div>
	);
};

const styles = {
	card: {
		display: 'flex',
		backgroundColor: '#fff',
		borderRadius: '15px',
		marginBottom: '15px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		border: '1px solid #f0f0f0'
	},
	imageContainer: {
		width: '80px',
		height: '80px',
		backgroundColor: '#f8f9fa',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'cover'
	},
	placeholder: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center'
	},
	content: {
		flex: 1,
		padding: '10px 15px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	amount: {
		margin: 0,
		fontSize: '16px',
		fontWeight: '700',
		color: '#172B4D'
	},
	subText: {
		margin: '2px 0',
		fontSize: '13px',
		color: '#6B778C'
	},
	date: {
		color: '#A5ADBA',
		fontSize: '11px'
	}
};
export default PaiementTabMobile;
